// Chat history of the IBKR chat panels, one independent chat per panel (keyed
// by the panel id): every newly opened window starts a new chat with its own
// agent session, and the chat is dropped when the window closes. Nothing is
// persisted in the browser; the backend keeps each session's conversation.

import { create } from "zustand";
import { ChatMessage, Widget } from "./types";

interface Chat {
  sessionId: string; // agent conversation id (backend: ibkr_agent.py, SQLite session)
  messages: ChatMessage[];
}

type ChatState = {
  chats: Record<string, Chat>;
  ensureChat: (chatId: string) => void;
  dropChat: (chatId: string) => void;
  addMessage: (chatId: string, message: Omit<ChatMessage, "id" | "ts" | "widgets"> & { widgets?: Omit<Widget, "id">[] }) => void;
  // an order / ladder window opened by a widget button, as its own assistant
  // message; not opened when a widget matching `exists` is already in the chat
  openWindow: (chatId: string, text: string, widget: Omit<Widget, "id">, options?: { exists?: (w: Widget) => boolean; liveIntro?: boolean }) => void;
  patchWidget: (chatId: string, widgetId: string, params: Record<string, any>) => void;
  duplicateWidget: (chatId: string, widgetId: string, params: Record<string, any>) => void;
  removeWidget: (chatId: string, widgetId: string) => void;
  removeWidgets: (chatId: string, match: (w: Widget) => boolean) => void;
};

export const newId = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const withId = (w: Omit<Widget, "id">): Widget => ({ ...w, id: newId() });

function mapMessages(state: ChatState, chatId: string, fn: (messages: ChatMessage[]) => ChatMessage[]) {
  const chat = state.chats[chatId];
  if (!chat) return state;
  return { chats: { ...state.chats, [chatId]: { ...chat, messages: fn(chat.messages) } } };
}

export const useIBKRChatStore = create<ChatState>()((set, get) => ({
  chats: {},

  ensureChat: (chatId) => {
    if (get().chats[chatId]) return;
    set((s) => ({ chats: { ...s.chats, [chatId]: { sessionId: `${chatId}-${newId()}`, messages: [] } } }));
  },

  dropChat: (chatId) =>
    set((s) => {
      const { [chatId]: _dropped, ...rest } = s.chats;
      return { chats: rest };
    }),

  addMessage: (chatId, message) =>
    set((s) =>
      mapMessages(s, chatId, (ms) => [
        ...ms,
        { ...message, id: newId(), ts: Date.now(), widgets: (message.widgets ?? []).map(withId) },
      ])
    ),

  openWindow: (chatId, text, widget, options) =>
    set((s) =>
      mapMessages(s, chatId, (ms) => {
        const exists = options?.exists;
        if (exists && ms.some((m) => m.widgets.some(exists))) return ms;
        return [
          ...ms,
          { id: newId(), role: "assistant", ts: Date.now(), text, widgets: [withId(widget)], local: true, liveIntro: options?.liveIntro },
        ];
      })
    ),

  patchWidget: (chatId, widgetId, params) =>
    set((s) =>
      mapMessages(s, chatId, (ms) =>
        ms.map((m) => ({
          ...m,
          widgets: m.widgets.map((w) => (w.id === widgetId ? { ...w, params: { ...w.params, ...params } } : w)),
        }))
      )
    ),

  // the copy becomes its own message (same intro text) right after the
  // original's message, with `params` as its fresh state
  duplicateWidget: (chatId, widgetId, params) =>
    set((s) =>
      mapMessages(s, chatId, (ms) =>
        ms.flatMap((m) => {
          const w = m.widgets.find((x) => x.id === widgetId);
          if (!w) return [m];
          const copy: ChatMessage = {
            id: newId(), role: "assistant", ts: Date.now(), text: m.text, local: true, liveIntro: m.liveIntro,
            widgets: [{ ...w, id: newId(), params }],
          };
          return [m, copy];
        })
      )
    ),

  removeWidget: (chatId, widgetId) => get().removeWidgets(chatId, (w) => w.id === widgetId),

  removeWidgets: (chatId, match) =>
    set((s) =>
      mapMessages(s, chatId, (ms) =>
        ms
          .map((m) => ({ ...m, widgets: m.widgets.filter((w) => !match(w)) }))
          .filter((m) => !(m.local && m.widgets.length === 0))
      )
    ),
}));
